// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import {User} from '@app/features/user/models/User';
import type {UserPrivate, User as WireUser} from '@fluxer/schema/src/domains/user/UserResponseSchemas';
import {makeAutoObservable, reaction, runInAction} from 'mobx';

const CURRENT_USER_PRIVATE_WIRE_KEYS = [
	'is_staff',
	'email',
	'email_bounced',
	'mfa_enabled',
	'phone',
	'authenticator_types',
	'verified',
	'premium_type',
	'premium_since',
	'premium_until',
	'premium_will_cancel',
	'premium_billing_cycle',
	'premium_lifetime_sequence',
	'registration_sequence',
	'premium_grace_ends_at',
	'premium_discriminator',
	'premium_badge_hidden',
	'premium_badge_masked',
	'premium_badge_timestamp_hidden',
	'premium_badge_sequence_hidden',
	'premium_purchase_disabled',
	'premium_enabled_override',
	'premium_perks_disabled',
	'password_last_changed_at',
	'last_voice_activity_sharing_change_at',
	'required_actions',
	'nsfw_allowed',
	'pending_bulk_message_deletion',
	'has_dismissed_premium_onboarding',
	'has_ever_purchased',
	'has_unread_gift_inventory',
	'unread_gift_inventory_count',
	'age_verified_adult',
	'terms_agreed_at',
	'privacy_agreed_at',
	'traits',
	'acls',
	'timezone',
	'timezone_privacy_flags',
] as const;

function isPublicOnlyCurrentUserPayload(user: WireUser): boolean {
	if (typeof user.mention_flags === 'number') {
		return false;
	}
	return !CURRENT_USER_PRIVATE_WIRE_KEYS.some((key) => key in user);
}

class Users {
	users: Record<string, User> = {};
	nameplateIds: Record<string, string | null> = {};
	avatarDecorationIds: Record<string, string | null> = {};
	profileFrameIds: Record<string, string | null> = {};
	userCount = 0;
	profileStamp = 0;

	constructor() {
		makeAutoObservable(
			this,
			{
				getUser: false,
				getNameplateId: false,
				getAvatarDecorationId: false,
				getProfileFrameId: false,
				getCurrentUser: false,
				getUserByTag: false,
				getUsers: false,
			},
			{autoBind: true},
		);
	}

	get currentUser(): User | null {
		const currentUserId = Authentication.userId;
		if (!currentUserId) {
			return null;
		}
		return this.users[currentUserId] ?? null;
	}

	get currentUserId(): string | null {
		return Authentication.userId;
	}

	get usersList(): ReadonlyArray<User> {
		return Object.values(this.users);
	}

	getUser(userId: string): User | undefined {
		return this.users[userId];
	}

	getNameplateId(userId: string): string | null | undefined {
		if (Object.prototype.hasOwnProperty.call(this.nameplateIds, userId)) {
			return this.nameplateIds[userId];
		}
		return this.users[userId]?.nameplateId;
	}

	setNameplateId(userId: string, nameplateId: string | null): void {
		const previous = this.getNameplateId(userId) ?? null;
		this.nameplateIds[userId] = nameplateId;
		const existingUser = this.users[userId];
		if (existingUser && existingUser.nameplateId !== nameplateId) {
			const nextUser = existingUser.withUpdates({id: userId, nameplate_id: nameplateId});
			if (!existingUser.equals(nextUser)) {
				this.users[userId] = nextUser;
			}
		}
		if (previous !== nameplateId) {
			this.profileStamp += 1;
		}
	}

	getAvatarDecorationId(userId: string): string | null | undefined {
		if (Object.prototype.hasOwnProperty.call(this.avatarDecorationIds, userId)) {
			return this.avatarDecorationIds[userId];
		}
		return this.users[userId]?.avatarDecorationId;
	}

	setAvatarDecorationId(userId: string, decorationId: string | null): void {
		const previous = this.getAvatarDecorationId(userId) ?? null;
		this.avatarDecorationIds[userId] = decorationId;
		const existingUser = this.users[userId];
		if (existingUser && existingUser.avatarDecorationId !== decorationId) {
			const nextUser = existingUser.withUpdates({id: userId, avatar_decoration_id: decorationId});
			if (!existingUser.equals(nextUser)) {
				this.users[userId] = nextUser;
			}
		}
		if (previous !== decorationId) {
			this.profileStamp += 1;
		}
	}

	getProfileFrameId(userId: string): string | null | undefined {
		if (Object.prototype.hasOwnProperty.call(this.profileFrameIds, userId)) {
			return this.profileFrameIds[userId];
		}
		return this.users[userId]?.profileFrameId;
	}

	setProfileFrameId(userId: string, frameId: string | null): void {
		const previous = this.getProfileFrameId(userId) ?? null;
		this.profileFrameIds[userId] = frameId;
		const existingUser = this.users[userId];
		if (existingUser && existingUser.profileFrameId !== frameId) {
			const nextUser = existingUser.withUpdates({id: userId, profile_frame_id: frameId});
			if (!existingUser.equals(nextUser)) {
				this.users[userId] = nextUser;
			}
		}
		if (previous !== frameId) {
			this.profileStamp += 1;
		}
	}

	getCurrentUser(): User | undefined {
		return this.currentUser ?? undefined;
	}

	getUserByTag(tag: string): User | undefined {
		return this.usersList.find((user) => user.tag === tag);
	}

	getUsers(): ReadonlyArray<User> {
		return this.usersList;
	}

	handleGatewayReady(currentUser: UserPrivate): void {
		const userRecord = new User(currentUser);
		this.users = {
			[currentUser.id]: userRecord,
		};
		this.nameplateIds = {
			[currentUser.id]: userRecord.nameplateId ?? null,
		};
		this.avatarDecorationIds = {
			[currentUser.id]: userRecord.avatarDecorationId ?? null,
		};
		this.profileFrameIds = {
			[currentUser.id]: userRecord.profileFrameId ?? null,
		};
		this.userCount = 1;
		this.profileStamp += 1;
		if (!userRecord.isClaimed()) {
			setTimeout(async () => {
				const {openClaimAccountModal} = await import('@app/features/auth/components/modals/ClaimAccountModal');
				openClaimAccountModal();
			}, 1000);
		}
	}

	handleUserUpdate(
		user: WireUser,
		options?: {
			clearMissingOptionalFields?: boolean;
		},
	): void {
		const existingUser = this.users[user.id];
		if (
			user.id === this.currentUserId &&
			existingUser &&
			options?.clearMissingOptionalFields !== true &&
			isPublicOnlyCurrentUserPayload(user)
		) {
			if ('nameplate_id' in user) {
				this.setNameplateId(user.id, user.nameplate_id ?? null);
			}
			if ('avatar_decoration_id' in user) {
				this.setAvatarDecorationId(user.id, user.avatar_decoration_id ?? null);
			}
			if ('profile_frame_id' in user) {
				this.setProfileFrameId(user.id, user.profile_frame_id ?? null);
			}
			return;
		}
		this.storeUser(existingUser, existingUser ? existingUser.withUpdates(user, options) : new User(user));
	}

	private storeUser(existingUser: User | undefined, nextUser: User): void {
		if (existingUser) {
			if (existingUser.equals(nextUser)) {
				return;
			}
		} else {
			this.userCount += 1;
		}
		this.users[nextUser.id] = nextUser;
		this.nameplateIds[nextUser.id] = nextUser.nameplateId ?? null;
		this.avatarDecorationIds[nextUser.id] = nextUser.avatarDecorationId ?? null;
		this.profileFrameIds[nextUser.id] = nextUser.profileFrameId ?? null;
		this.profileStamp += 1;
	}

	cacheUsers(
		users: Array<
			WireUser & {
				globalName?: never;
			}
		>,
	): void {
		runInAction(() => {
			for (const user of users) {
				const existingUser = this.users[user.id];
				if (user.id === this.currentUserId && existingUser && isPublicOnlyCurrentUserPayload(user)) {
					if ('nameplate_id' in user) {
						this.setNameplateId(user.id, user.nameplate_id ?? null);
					}
					if ('avatar_decoration_id' in user) {
						this.setAvatarDecorationId(user.id, user.avatar_decoration_id ?? null);
					}
					if ('profile_frame_id' in user) {
						this.setProfileFrameId(user.id, user.profile_frame_id ?? null);
					}
					continue;
				}
				this.storeUser(existingUser, existingUser ? existingUser.withUpdates(user) : new User(user));
			}
		});
	}

	subscribe(callback: () => void): () => void {
		return reaction(
			() => this.userCount,
			() => callback(),
			{fireImmediately: true},
		);
	}
}

export default new Users();
